import { useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Theme from "../../components/Theme";
const Hero = dynamic(() =>
  import("../../components/organisms/industries/Hero")
);
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const FAQ = dynamic(() => import("../../components/organisms/common/FAQ"));
const Masonry = dynamic(() =>
  import("../../components/organisms/industries/Masonry")
);
const CTA = dynamic(() => import("../../components/organisms/common/CTA"));
const FeaturedImage = dynamic(() =>
  import("../../components/organisms/common/FeaturedImage")
);
const CutoutFeaturedImage = dynamic(() =>
  import("../../components/organisms/common/CutoutFeaturedImage")
);
const CtaSecondary = dynamic(() =>
  import("../../components/organisms/common/CtaSecondary")
);
const IntegrationCards = dynamic(() =>
  import("../../components/organisms/common/IntegrationCards")
);
const FeaturesGrid = dynamic(() =>
  import("../../components/organisms/common/FeaturesGrid")
);
const FeaturedText = dynamic(() =>
  import("../../components/organisms/common/FeaturedText")
);
const BoxShadowCard = dynamic(() =>
  import("../../components/organisms/common/BoxShadowCard")
);
const CtaSecondaryWithoutShadow = dynamic(() =>
  import("../../components/organisms/industries/CtaSecondaryWithoutShadow")
);
const DoubleCTA = dynamic(() =>
  import("../../components/organisms/common/DoubleCTA")
);
const Schema = dynamic(() =>
  import("../../components/organisms/common/Schema")
);
const AppStorage = dynamic(() => import("../../components/AppStorage"));

const TpWidget = dynamic(() =>
  import("../../components/atoms/document/TpWidget")
);
const TpWidgetSlider = dynamic(() =>
  import("../../components/organisms/common/TpWidgetSlider")
);
const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);

// Local Components
export const SecondFold = ({ data, isNewDesign }) => {
  return (
    <CutoutFeaturedImage
      data={data}
      className={`md:pb-10 md:pt-5 ${isNewDesign ? "lg:pb-15" : "lg:pb-20"} `}
    />
  );
};
export const FeaturesGridSection = ({ data, isIconsInline }) => {
  return (
    <section
      className={`bg-light lg:pb-20 md:pb-10 ${isIconsInline ? "" : "lg:pt-4"}`}
    >
      <FeaturesGrid
        data={data.list}
        colCount={data.colCount}
        isIconsInline={isIconsInline}
      />
    </section>
  );
};
export const MasonrySection = ({ data, isNewDesign }) => {
  return (
    <section className="industry-masonry bg-offset">
      <h2
        className={`people-love-setmore container text-xl text-center md:mb-5 ${
          isNewDesign ? `lg:mb-10` : `lg:mb-9`
        }`}
      >
        <span>{data.title}</span>{" "}
        <span className="md:d-block heart-glyph lg:ml-1 lg:mr-1">❤️</span>{" "}
        <span>Setmore</span>
      </h2>
      <TpWidgetSlider />
      <Masonry
        data={data.list}
        isNewDesign={isNewDesign}
        wrapClass={`${isNewDesign ? "lg:mt-10" : ""}`}
      />
    </section>
  );
};
export const TopIntegrations = ({ data, isNewDesign = false }) => {
  return (
    <section className={`lg:pb-10 md:pb-11 ${isNewDesign ? "lg:pb-20" : ""}`}>
      <div className="container">
        <div className="lg:maxw-xl lg:px-5 lg:text-center md:text-left mx-auto">
          <h2
            className={`text-lg lg:mb-5 md:mb-5 ${
              isNewDesign ? "" : "mb:pr-1"
            }`}
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
        </div>
        <IntegrationCards data={data.list} />
        <div className="container lg:mt-5 md:mt-6 text-center">
          <Link
            href={data.seeAll.href}
            prefetch={false}
            className="see-all text-brandcolor"
            id={data.seeAll.id}
          >
            <span className="text-center w-100">{data.seeAll.text}</span>
          </Link>
        </div>
      </div>
    </section>
  );
};
export const BoxShadowCardSection = ({ data, className }) => {
  return (
    <section className={className}>
      <h2
        className="container text-xl lg:text-center md:text-left lg:mb-10 md:mb-5 lg:maxw-2xl"
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
      <BoxShadowCard data={data.list} hasLink={data.hasLink} />
    </section>
  );
};
export const BoxShadowCardSmallIconSection = ({
  data,
  className,
  pageName,
}) => {
  return (
    <section className={className}>
      <h2
        className={`container lg:text-center md:text-center lg:mb-10 md:mb-5  ${
          pageName === "industries--salon" ? "lg:maxw-2xl" : "lg:maxw-xl"
        } `}
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
      <BoxShadowCard
        data={data.list}
        hasLink={data.hasLink}
        isIconsInline={!(pageName === "industries--salon")}
      />
      <style jsx>{`
        h2 {
          font-size: ${font.size.lg[4]} !important;
          line-height: ${lineHeight[16]} !important;
        }
        @media (max-width: ${viewport.mobile.md}) {
          h2 {
            font-size: ${font.size.lg[1]} !important;
            line-height: ${lineHeight[10]} !important;
            text-align: center !important;
          }
        }
      `}</style>
    </section>
  );
};

// Medical pages
const medicalPages = [
  "chiropractor",
  "medical-appointment-scheduling-software",
  "doctors",
  "counseling",
  "dentist",
  "physical-therapy",
  "clinics",
  "veterinary",
  "vaccination-scheduling",
];

// ChatSupport Logic
const pagesWithChatSupportScript = [
  "barber-shops",
  "bridal-salon",
  "sales",
  "dentist",
  "salon",
  "consulting",
];

//New design pages
const newDesignPages = [
  "call-center",
  "retail-scheduling-software",
  "massage-therapy",
  "spa",
  "government",
  "veterinary",
  "electrical",
  "cleaning-services",
  "landscaping",
  "customer-service",
  "plumbing",
  "music-lessons",
  "clinics",
  "hvac",
  "library",
  "lashes-eyebrows",
];

export async function getServerSideProps({ req, params, locale }) {
  try {
    // Variables
    let allData = {};
    let { page } = params;
    let marketingResponse, contentResponse;
    // Get Country Name
    const country = req.headers["x-geo-country"] || "IN";
    // Fetch All Path
    const routesRes = await fetch(`${process.env.JSON_PATH}/routes.json`);
    if (!routesRes.ok) throw new Error("Failed to fetch routes");
    const routesData = await routesRes.json();

    // Specific Paths
    let routes = routesData.industries;

    // 404 Redirection
    if (!routes.includes(page)) {
      return {
        notFound: true,
      };
    }

    if (req.originalUrl.startsWith("/en")) {
      return {
        redirect: {
          destination: req.url.replace("/en", ""),
          permanent: true,
        },
      };
    }

    // Fetch
    const headerResponse = await fetch(
      `${process.env.JSON_PATH}/${locale}/common/header.json`
    );
    if (!headerResponse.ok) throw new Error("Failed to fetch header");

    const footerResponse = await fetch(
      `${process.env.JSON_PATH}/${locale}/common/footer.json`
    );
    if (!footerResponse.ok) throw new Error("Failed to fetch footer");

    // Convert
    allData.header = await headerResponse.json();
    allData.footer = await footerResponse.json();

    // Decide the path for marketing and content fetch
    let contentPath = `${process.env.JSON_PATH}/${locale}/industries/${page}`;

    if (country === "US" && medicalPages.includes(page)) {
      contentPath += "/us";
      // Start now rewrite
      allData.header.otherItems.startnow.text = "Start now";
    }
    // Fetch
    marketingResponse = await fetch(`${contentPath}/marketing.json`);
    contentResponse = await fetch(`${contentPath}/content.json`);

    // Catch Error
    if (!marketingResponse.ok || !contentResponse.ok) {
      return {
        notFound: true,
      };
    }

    // Convert
    allData.marketing = await marketingResponse.json();
    allData.content = await contentResponse.json();

    // Wrap
    const header = {
      header: allData.header,
    };
    const footer = {
      footer: allData.footer,
    };

    // Combine all data
    const data = {
      ...header,
      ...footer,
      ...allData.marketing,
      ...allData.content,
    };

    return {
      props: {
        data,
        pageName: params.page,
        lang: locale,
        fallback: false,
      },
    };
  } catch (error) {
    console.error("Error in getServerSideProps:", error);
    return {
      notFound: true,
    };
  }
}

export default function Industry({ data, pageName, lang, nonce }) {
  const { header, footer, seo, content } = data;
  const ShowChatSupport = () => {
    return (
      <span>
        <style jsx global>{`
          .chatsupport-button {
            visibility: visible !important;
          }
        `}</style>
      </span>
    );
  };

  // People Section BG Color Logic
  let peopleSectionClassName = "bg-light";
  if (pageName === "barber-shops") {
    peopleSectionClassName = "";
  }

  // CTA BG Color Logic
  let ctaSecondaryClassName = "bg-light";
  if (
    pageName === "industry-muas" ||
    pageName === "clinics" ||
    pageName === "veterinary"
  ) {
    ctaSecondaryClassName = "";
  }

  // FAQ Section BG Color Logic
  let faqSectionClassName = "";
  if (
    content.hasOwnProperty("industrySpecificFeatures") &&
    content.hasOwnProperty("faqs")
  ) {
    faqSectionClassName = "bg-light";
  }

  const isNewDesignBool = newDesignPages.includes(pageName);

  const pagesWithoutTrustPilotWidget = ["physical-therapy", "embassies"];

  useEffect(() => {
    if (!pagesWithoutTrustPilotWidget.includes(pageName)) {
      window.focus();

      var listenGTMTriggerInIframe = window.addEventListener(
        "blur",
        function () {
          var widgetMicro = document.querySelectorAll("iframe")[0];
          var widgetCarousel = document.querySelectorAll("iframe")[1];
          const regex = /^(\/industries\/[\w-]+)$/i;
          const inIndustrySpecificPage = regex.test(window.location.pathname);
          if (
            inIndustrySpecificPage &&
            (document.activeElement == widgetMicro ||
              document.activeElement == widgetCarousel)
          ) {
            // Add to dataLayer your information
            window.dataLayer = window.dataLayer || [];
            let trustPilotTrackingId = content.helpers.trustPilotTrackingId;
            let trustPilotDataObject = {};
            trustPilotDataObject[trustPilotTrackingId] = true;
            trustPilotDataObject.event = trustPilotTrackingId;
            window.dataLayer.push(trustPilotDataObject);
          }
          window.removeEventListener("blur", listenGTMTriggerInIframe);
        }
      );
      return () => {
        window.removeEventListener("blur", listenGTMTriggerInIframe);
      };
    }
  });
  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className={`industry-page-with-trust-pilot ${content.helpers.pagename}`}
        seo={seo}
        nonce={nonce}
      >
        {medicalPages.includes(pageName) ? (
          <AppStorage
            pageName={`Industries Medical Page - ${pageName}`}
            isHipaaPage="true"
          />
        ) : (
          <AppStorage pageName={`Industries Sub Page - ${pageName}`} />
        )}
        <Hero data={content.hero} page={pageName} />
        <SecondFold
          data={content.cutoutFeaturedImage}
          isNewDesign={isNewDesignBool}
        />
        <FeaturesGridSection
          data={content.featuresGrid}
          isIconsInline={isNewDesignBool}
        />
        <MasonrySection data={content.masonry} isNewDesign={isNewDesignBool} />
        <CTA
          data={content.cta}
          className="lg:mt-20 lg:mb-20 md:mt-10 md:mb-10"
          page="industries"
          colorScheme={
            content.helpers.pageId === "industries--salon"
              ? null
              : isNewDesignBool && "grey-white-blue"
          }
          ctaBgLightGreenColor={
            content.helpers.pageId === "medical" ||
            content.helpers.pageId === "industries--legal"
          }
        />
        {content.hasOwnProperty("topIntegrations") && (
          <TopIntegrations
            data={content.topIntegrations}
            isNewDesign={isNewDesignBool}
          />
        )}
        <section
          className={`md:mb-10 ${
            content.hasOwnProperty("featuredImageSections") ? "lg:pt-10" : ""
          }`}
        >
          {content.featuredImageSections &&
            content.featuredImageSections.map((item, index) => {
              return (
                <FeaturedImage
                  key={index}
                  data={item}
                  imageAlign={item.image.align}
                />
              );
            })}
          {content.featuredTextSections &&
            content.featuredTextSections.map((item, index) => {
              return <FeaturedText key={index} data={item} />;
            })}
        </section>
        {content.hasOwnProperty("ctaSecondary") && (
          <CtaSecondary
            data={content.ctaSecondary}
            className="lg:mt-20 lg:mb-20 md:mb-10 md:mt-10"
          />
        )}
        {content.hasOwnProperty("ctaTwo") && (
          <CTA
            data={content.ctaTwo}
            className="lg:mt-20 lg:mb-20 md:mt-10 md:mb-10"
            page="industries"
            colorScheme={isNewDesignBool ? "grey-white-blue" : ""}
          />
        )}
        {content.hasOwnProperty("doubleCTA") && (
          <>
            <DoubleCTA
              data={content.doubleCTA}
              className="lg:mt-20 lg:mb-20 md:mt-10 md:mb-10"
            />
          </>
        )}
        {content.hasOwnProperty("ctaSecondaryWithoutShadow") && (
          <CtaSecondaryWithoutShadow
            data={content.ctaSecondaryWithoutShadow}
            className={ctaSecondaryClassName}
          />
        )}
        {content.hasOwnProperty("industrySpecificFeatures") &&
          !isNewDesignBool && (
            <BoxShadowCardSection
              data={content.industrySpecificFeatures}
              className="industry-features bg-offset bg-offset--adapt pb-10"
            />
          )}
        {content.hasOwnProperty("industrySpecificFeatures") &&
          isNewDesignBool && (
            <BoxShadowCardSmallIconSection
              data={content.industrySpecificFeatures}
              pageName={content.helpers.pageId}
              className="industry-features bg-offset bg-offset--adapt lg:pb-10 md:pb-1"
            />
          )}
        {content.hasOwnProperty("faqs") && (
          <>
            <FAQ
              data={content.faqs}
              className={` md:pb-10 pt-10 faq-section bg-offset-top ${faqSectionClassName} ${
                isNewDesignBool ? "lg:pd-20" : "lg:pb-15"
              }`}
              isNewDesign={isNewDesignBool}
            />
            <Schema data={content.faqs.list} />
          </>
        )}
        <style global jsx>{`
          .industry-page-with-trust-pilot .cta-w-image {
            gap: 40px 20px;
          }
          .industry-page-with-trust-pilot .cta-w-image article:nth-child(1) {
            background-color: ${colors.primitive.white.default} !important;
            box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2),
              0 1px 3px 0 rgba(48, 58, 71, 0.1);
          }
          .industry-page-with-trust-pilot .cta-w-image article:nth-child(2) a {
            background-color: transparent;
            color: ${colors.primitive.green[800]};
            border: 1px solid ${colors.primitive.green[800]};
          }
          .industry-page-with-trust-pilot .cta-w-image article:nth-child(2) {
            background-color: ${colors.primitive.grey[600]} !important;
          }
          .medical .cta-light-green-theme a.btn-lg.btn-ternary,
          .legal-industry .cta-light-green-theme a.btn-lg.btn-ternary {
            background-color: ${colors.primitive.green[800]} !important;
            color: ${colors.primitive.white.default} !important;
          }
          .medical .cta-light-green-theme a.btn-lg.btn-ternary:hover,
          .legal-industry .cta-light-green-theme a.btn-lg.btn-ternary:hover {
            background-color: ${colors.primitive.green[900]} !important;
          }
          .medical .cta-contrast.has-image h2,
          .legal-industry .cta-contrast.has-image h2 {
            font-size: ${font.size.lg[1]} !important;
            line-height: ${lineHeight[10]} !important;
          }
          @media (min-width: ${viewport.tablet.sm}) and (max-width: ${viewport
              .desktop.sm}) {
            .legal-industry .cta-contrast.has-image.has-img-offset picture {
              right: 20px !important;
            }
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .industry-page-with-trust-pilot .industry-masonry .container {
              margin-left: 20px !important;
              margin-right: 20px !important;
              width: initial !important;
            }
            .industry-page-with-trust-pilot
              .card--default:not(.top-integrations) {
              margin-left: 20px !important;
              margin-right: 20px !important;
              width: initial !important;
            }
            .industry-page-with-trust-pilot
              .industry-masonry
              .card--masonry
              > ul {
              max-width: none !important;
            }
          }
          ${isNewDesignBool
            ? `
            .industry-page-with-trust-pilot .bg-light,
            .industry-page-with-trust-pilot .industry-masonry.bg-offset:after,
            .industry-page-with-trust-pilot .industry-features.bg-offset:after{
              background-color: ${colors.primitive.grey[600]} !important;
            }
            .industry-page-with-trust-pilot .industry-masonry.bg-offset:after{
              height: 361px;
            }
            .industry-page-with-trust-pilot .industry-features.bg-offset:after{
              height: calc(100% - 223px);
            }
            .industry-page-with-trust-pilot .use-setmore-wrap .imgholder-quinary{
              margin-top: -40px;
            }
            .industry-page-with-trust-pilot .cta-contrast picture {
              right: 99px !important;
            }
            .industry-page-with-trust-pilot .cta-contrast.has-noimage article.mx-auto{
              max-width: 760px;
            }
            .top-integrations{
              margin-left: initial;
              margin-right: initial;
            }
            .people-love-setmore * {
              font-size: ${font.size.lg[4]} !important;
              line-height: ${lineHeight[16]} !important;
            }
            @media (max-width: ${viewport.mobile.md}) {
              .industry-features.bg-offset::after {
                height: 700px;
              }
              .people-love-setmore * {
                font-size: ${font.size.lg[1]} !important;
                line-height: ${lineHeight[10]} !important;
              }
              .people-love-setmore > span {
                display: block !important;
              }
              .people-love-setmore .heart-glyph,
              .people-love-setmore .heart-glyph + span {
                display: inline-block !important;
              }
              .industry-page-with-trust-pilot .industry-masonry.bg-offset:after{
                height: 503px;
              }
              .industry-page-with-trust-pilot .use-setmore-wrap .imgholder-quinary{
                margin-top: -55px;
              }
            }
            @media (min-width: ${viewport.tablet.sm}) {
              .use-setmore-wrap article {
                position: relative;
                top: 40px;
              }
              .industry-page-with-trust-pilot .industry-masonry.bg-offset:after{
                height: 440px !important;
              }
              ${
                pageName === "lashes-eyebrows" && lang === "pt"
                  ? `.cta-contrast.has-image article{
                    max-width: 470px;
                  }`
                  : ""
              }

              ${
                pageName === "hvac" && lang !== "en"
                  ? `.cta-contrast.has-image article{
                    max-width: 590px;
                  }`
                  : ""
              }
               .cta-contrast.has-image{
                padding: 80px 100px !important;
              }
              .cta-contrast.has-image h2 {
                font-size: ${font.size.lg[1]} !important;
                line-height: ${lineHeight[10]} !important;
              }
              .faq-section {
                padding-bottom: 160px !important;
              }
            }
          `
            : ``}
        `}</style>
        {content.helpers.pageId !== "industries--salon" && !isNewDesignBool && (
          <People className={peopleSectionClassName} />
        )}

        <WideCTA data={content.wideCTA} />
        <TpWidget />
        {pagesWithChatSupportScript.includes(pageName) && (
          <>
            <InjectChatSupport />
            <ShowChatSupport />
          </>
        )}
      </Theme>
    </>
  );
}
